/* ==========================================================================
   Login Page
   Task F11/F19: Provider login with slug-based routing
   Task F30: Updated to display pharmacy logo from portal config
   ========================================================================== */

import { useState, useEffect } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Checkbox } from '@/components/ui/Checkbox'
import { Alert } from '@/components/ui/Alert'
import { PharmacyLogo } from '@/components/ui/PharmacyLogo'
import { TwoFactorCodeStep } from '@/components/auth/TwoFactorCodeStep'
import { AccountNotSetUpNotice } from '@/components/auth/AccountNotSetUpNotice'
import { useAuth } from '@/hooks/useAuth'
import { usePortalSlug } from '@/context/PortalContext'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface FormErrors {
  email?: string
  password?: string
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function validateEmail(email: string): string | undefined {
  if (!email.trim()) {
    return 'Email is required'
  }
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }
  return undefined
}

function validatePassword(password: string): string | undefined {
  if (!password) {
    return 'Password is required'
  }
  return undefined
}

function validateForm(email: string, password: string): FormErrors {
  return {
    email: validateEmail(email),
    password: validatePassword(password),
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const slug = usePortalSlug()

  // Default redirect to prescription form
  const redirectTo = searchParams.get('redirect') || `/rx/${slug}/prescribe`

  const {
    login,
    verifyTwoFactor,
    cancelTwoFactor,
    twoFactorPending,
    loading,
    error: authError,
    clearError,
    isAuthenticated,
  } = useAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTo])

  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Handle field blur for validation
  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    if (field === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(email) }))
    } else {
      setErrors((prev) => ({ ...prev, password: validatePassword(password) }))
    }
  }

  // Clear field error when user starts typing
  const handleEmailChange = (value: string) => {
    setEmail(value)
    clearError()
    if (touched.email) {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
    }
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    clearError()
    if (touched.password) {
      setErrors((prev) => ({ ...prev, password: validatePassword(value) }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate all fields
    const formErrors = validateForm(email, password)
    setErrors(formErrors)
    setTouched({ email: true, password: true })

    // Check for validation errors
    if (formErrors.email || formErrors.password) {
      return
    }

    // Attempt login
    const outcome = await login(email, password, rememberMe)
    if (outcome === 'signed_in') {
      navigate(redirectTo, { replace: true })
    }
  }

  // Second step (PORTAL-187): the password was right, now the authenticator code.
  const handleTwoFactorSubmit = async (code: string, rememberDevice: boolean) => {
    const success = await verifyTwoFactor(code, rememberDevice)
    if (success) {
      navigate(redirectTo, { replace: true })
    }
  }

  const hasErrors = errors.email || errors.password

  if (twoFactorPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <PharmacyLogo size="xl" linkTo="/" />
          </div>
          <Card className="p-6 sm:p-8">
            <TwoFactorCodeStep
              onSubmit={handleTwoFactorSubmit}
              onCancel={cancelTwoFactor}
              loading={loading}
              error={authError}
            />
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        {/* Pharmacy Logo from portal config (Task F30) */}
        <div className="flex justify-center mb-8">
          <PharmacyLogo size="xl" linkTo="/" />
        </div>

        <Card className="p-6 sm:p-8">
          {/* Title */}
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-6">
            Welcome back
          </h1>

          {/* Auth error alert */}
          {authError && (
            <Alert variant="error" className="mb-6">
              {authError}
            </Alert>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              {/* Email field */}
              <FormField
                label="Email"
                required
                error={touched.email ? errors.email : undefined}
                htmlFor="email"
              >
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  error={touched.email && !!errors.email}
                  disabled={loading}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
              </FormField>

              {/* Password field with show/hide toggle */}
              <FormField
                label="Password"
                required
                error={touched.password ? errors.password : undefined}
                htmlFor="password"
              >
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    error={touched.password && !!errors.password}
                    disabled={loading}
                    className="pr-12"
                    aria-describedby={
                      errors.password ? 'password-error' : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormField>

              {/* Remember me checkbox */}
              <Checkbox
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading || !!hasErrors}
              className="w-full mt-6"
            >
              Sign In
            </Button>
          </form>

          {/* Forgot password link - uses pharmacy primary color (Task F30) */}
          <div className="mt-6 text-center">
            <Link
              to={`/rx/${slug}/forgot-password`}
              className="text-sm text-[var(--pharmacy-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
            >
              Forgot password?
            </Link>
          </div>

          {/* Guidance for providers without an account (PORTAL-198) */}
          <AccountNotSetUpNotice className="mt-4" />
        </Card>

        {/* Pharmacy staff admin login link - subtle, out of providers' way */}
        <div className="mt-4 text-center">
          <Link
            to={`/rx/${slug}/admin/login`}
            className="text-xs text-charcoal/40 hover:text-charcoal/60 transition-colors"
          >
            Pharmacy Staff Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Login
